// Leitor/escritor de CSV DELIMITADO SEM CABECALHO.
//
// Por que nao reusar `_shared/csv.ts` da API: aquele parser exige linha de
// cabecalho e normaliza o separador. Os arquivos que o ERP de folha municipal
// gera nao tem cabecalho — a identidade do campo e' a POSICAO DA COLUNA, igual
// num layout posicional, so que delimitado. Tratar a primeira linha como header
// comeria um servidor de verdade.
//
// A disciplina e' a mesma do fixed-width.ts: o layout e' um ARRAY declarativo de
// campos, e dele saem o parser, o escritor E o documento tecnico. Nao ha slice
// de coluna espalhado pelo codigo.
/** Quebra o texto em linhas de celulas. Tolera CRLF, campo entre aspas com o
 *  separador dentro, aspas escapadas e BOM. Linha vazia e' descartada. */
export function lerLinhasDelimitadas(texto, sep = ";") {
    const limpo = texto.replace(/^﻿/, "");
    const linhas = [];
    let celulas = [];
    let atual = "";
    let dentroDeAspas = false;
    const fecharCelula = () => { celulas.push(atual); atual = ""; };
    const fecharLinha = () => {
        fecharCelula();
        // Linha inteiramente vazia nao vira registro.
        if (!(celulas.length === 1 && celulas[0].trim() === ""))
            linhas.push(celulas);
        celulas = [];
    };
    for (let i = 0; i < limpo.length; i++) {
        const c = limpo[i];
        if (dentroDeAspas) {
            if (c === '"') {
                if (limpo[i + 1] === '"') {
                    atual += '"';
                    i++;
                }
                else
                    dentroDeAspas = false;
            }
            else
                atual += c;
            continue;
        }
        if (c === '"') {
            dentroDeAspas = true;
            continue;
        }
        if (c === sep) {
            fecharCelula();
            continue;
        }
        if (c === "\n") {
            fecharLinha();
            continue;
        }
        if (c === "\r") {
            if (limpo[i + 1] === "\n")
                i++;
            fecharLinha();
            continue;
        }
        atual += c;
    }
    if (atual !== "" || celulas.length > 0)
        fecharLinha();
    return linhas;
}
/**
 * Le dinheiro nas formas que aparecem de verdade nos arquivos de folha.
 *
 * O arquivo de consignados vem `000000000496,53` (zeros a esquerda, virgula
 * decimal) e o de rubricas vem `16.97` (ponto decimal). O mesmo pacote pode
 * trazer as duas formas, entao nao da pra fixar um separador.
 *
 * Regra, igual a de `_shared/numero.ts` (parseNumeroBr) que os layouts CSV
 * antigos ja usam: havendo virgula, ela e' o decimal e o ponto e' milhar. Sem
 * virgula, ponto seguido de exatamente 3 digitos e' milhar; com 1 ou 2 digitos
 * e' decimal.
 */
export function lerDinheiro(bruto) {
    const t = bruto.trim().replace(/R\$\s*/i, "").replace(/\s/g, "");
    if (t === "")
        return null;
    let normal;
    if (t.includes(",")) {
        normal = t.replace(/\./g, "").replace(",", ".");
    }
    else if (/\.\d{3}$/.test(t) && (t.match(/\./g) ?? []).length >= 1 && !/\.\d{1,2}$/.test(t)) {
        normal = t.replace(/\./g, "");
    }
    else {
        normal = t;
    }
    if (!/^-?\d*\.?\d*$/.test(normal) || normal === "" || normal === ".")
        return null;
    const n = Number(normal);
    return Number.isFinite(n) ? n : null;
}
/** `07082024` -> `2024-08-07`. Devolve null quando nao for data real. */
export function lerDataBr(bruto) {
    const d = bruto.trim().replace(/\D/g, "");
    if (d.length !== 8)
        return null;
    const dia = Number(d.slice(0, 2)), mes = Number(d.slice(2, 4)), ano = Number(d.slice(4, 8));
    const dt = new Date(Date.UTC(ano, mes - 1, dia));
    if (dt.getUTCFullYear() !== ano || dt.getUTCMonth() !== mes - 1 || dt.getUTCDate() !== dia)
        return null;
    return `${d.slice(4, 8)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
}
/** Le uma linha de celulas conforme o layout de colunas. Nao lanca: devolve os
 *  erros pra quem chama decidir o regime (rejeita a linha ou o arquivo). */
export function parsearLinhaColunas(campos, celulas) {
    const erros = [];
    const valores = {};
    for (const campo of campos) {
        if (campo.kind === "ignorado")
            continue;
        const bruto = (celulas[campo.coluna - 1] ?? "").trim();
        const vazio = bruto === "";
        if (vazio && campo.obrigatorio) {
            erros.push({ campo: campo.nome, coluna: campo.coluna, mensagem: "obrigatorio e veio vazio" });
        }
        switch (campo.kind) {
            case "alfa":
                valores[campo.nome] = bruto;
                break;
            case "cpf": {
                if (vazio) {
                    valores[campo.nome] = "";
                    break;
                }
                const digitos = bruto.replace(/\D/g, "");
                if (digitos.length !== 11) {
                    erros.push({
                        campo: campo.nome, coluna: campo.coluna,
                        mensagem: `CPF invalido: "${bruto}" tem ${digitos.length} digito(s), esperado 11`,
                    });
                    valores[campo.nome] = "";
                    break;
                }
                // Normaliza aqui, uma vez: quem le `CargaLinha.cpf` recebe sempre a
                // forma nua, e nao precisa lembrar de tirar a mascara.
                valores[campo.nome] = digitos;
                break;
            }
            case "num": {
                if (vazio) {
                    valores[campo.nome] = 0;
                    break;
                }
                if (!/^\d+$/.test(bruto)) {
                    erros.push({ campo: campo.nome, coluna: campo.coluna, mensagem: `esperado apenas digitos, veio "${bruto}"` });
                    valores[campo.nome] = 0;
                    break;
                }
                valores[campo.nome] = Number(bruto);
                break;
            }
            case "dinheiro": {
                if (vazio) {
                    valores[campo.nome] = 0;
                    break;
                }
                const v = lerDinheiro(bruto);
                if (v === null) {
                    erros.push({ campo: campo.nome, coluna: campo.coluna, mensagem: `valor invalido: "${bruto}"` });
                    valores[campo.nome] = 0;
                    break;
                }
                valores[campo.nome] = v;
                break;
            }
            case "dataBr": {
                if (vazio) {
                    valores[campo.nome] = null;
                    break;
                }
                const d = lerDataBr(bruto);
                if (d === null) {
                    erros.push({ campo: campo.nome, coluna: campo.coluna, mensagem: `data invalida: "${bruto}" (esperado DDMMAAAA)` });
                }
                valores[campo.nome] = d;
                break;
            }
            case "competencia": {
                if (vazio) {
                    valores[campo.nome] = "";
                    break;
                }
                if (!/^\d{6}$/.test(bruto)) {
                    erros.push({ campo: campo.nome, coluna: campo.coluna, mensagem: `competencia invalida: "${bruto}" (esperado AAAAMM)` });
                    valores[campo.nome] = "";
                    break;
                }
                const mes = Number(bruto.slice(4, 6));
                if (mes < 1 || mes > 12) {
                    erros.push({ campo: campo.nome, coluna: campo.coluna, mensagem: `mes ${mes} nao existe` });
                }
                valores[campo.nome] = bruto;
                break;
            }
            case "codigo": {
                const v = bruto.toUpperCase();
                if (!vazio && campo.codigos && !campo.codigos.includes(v)) {
                    erros.push({
                        campo: campo.nome, coluna: campo.coluna,
                        mensagem: `codigo "${v}" nao existe (aceitos: ${campo.codigos.join(", ")})`,
                    });
                }
                valores[campo.nome] = v;
                break;
            }
        }
    }
    return { valores, erros };
}
/** Escreve uma celula, protegendo separador, aspas e quebra de linha. */
function escaparCelula(v, sep) {
    return v.includes(sep) || v.includes('"') || /[\r\n]/.test(v)
        ? `"${v.replace(/"/g, '""')}"`
        : v;
}
/** Monta uma linha delimitada a partir do layout. Coluna declarada e nao
 *  fornecida sai vazia; coluna `ignorado` tambem — o arquivo precisa manter a
 *  posicao das colunas que o outro lado espera. */
export function montarLinhaDelimitada(campos, valores, sep = ";", 
/** Separador decimal dos campos `dinheiro`. Ponto por padrao; a ALECE le virgula. */
decimal = ".") {
    const maxColuna = campos.reduce((m, c) => Math.max(m, c.coluna), 0);
    const celulas = Array.from({ length: maxColuna }, () => "");
    for (const campo of campos) {
        if (campo.kind === "ignorado")
            continue;
        const v = valores[campo.nome];
        if (v === null || v === undefined)
            continue;
        celulas[campo.coluna - 1] = escaparCelula(campo.kind === "dinheiro"
            ? (Math.round(Number(v) * 100) / 100).toFixed(2).replace(".", decimal)
            : String(v), sep);
    }
    return celulas.join(sep);
}
/** Confere que o layout numera colunas de 1..N sem buraco nem repeticao.
 *  Chamado nos testes de cada layout: coluna duplicada faz um campo sobrescrever
 *  o outro em silencio. */
export function validarLayoutColunas(campos) {
    const problemas = [];
    const vistas = new Map();
    for (const c of campos) {
        if (c.coluna < 1)
            problemas.push(`"${c.nome}": coluna ${c.coluna}`);
        const anterior = vistas.get(c.coluna);
        if (anterior)
            problemas.push(`coluna ${c.coluna} declarada duas vezes: "${anterior}" e "${c.nome}"`);
        vistas.set(c.coluna, c.nome);
    }
    const max = campos.reduce((m, c) => Math.max(m, c.coluna), 0);
    for (let i = 1; i <= max; i++) {
        if (!vistas.has(i))
            problemas.push(`coluna ${i} nao declarada (declare como "ignorado" se o arquivo a tem)`);
    }
    return problemas;
}
//# sourceMappingURL=csv-delimitado.js.map